
/**
 * Utility functions for URL manipulation and formatting
 */

/**
 * Creates a URL-friendly slug from a string
 * @param text The text to convert to a slug
 * @returns A URL-friendly slug
 */
export function createSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special chars except whitespace and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim(); // Remove leading and trailing whitespace
}

/**
 * Builds absolute URL from relative path
 * @param relativePath The relative path
 * @returns The absolute URL
 */
export function getAbsoluteUrl(relativePath: string): string {
  // If already an absolute URL, return as is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }
  
  // Get base URL from current location
  const baseUrl = window.location.origin;
  
  // Normalize the path to have a leading slash
  const normalizedPath = relativePath.startsWith('/') 
    ? relativePath 
    : `/${relativePath}`;
  
  // Combine base URL with normalized path
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Extract and normalize query parameters from URL
 * @param queryString The query string to parse
 * @returns An object with the parsed query parameters
 */
export function parseQueryParams(queryString: string): Record<string, string> {
  const params = new URLSearchParams(queryString);
  const result: Record<string, string> = {};
  
  params.forEach((value, key) => {
    result[key] = value;
  });
  
  return result;
}

/**
 * Creates a properly formatted URL for sharing
 * @param path The path to format for sharing
 * @returns A fully qualified absolute URL suitable for sharing
 */
export function createShareableUrl(path: string): string {
  const absoluteUrl = getAbsoluteUrl(path);
  
  // Ensure the URL is properly encoded
  try {
    // Parse the URL to handle any encoding issues
    const url = new URL(absoluteUrl);
    return url.toString();
  } catch (e) {
    // If URL parsing fails, return the original absoluteUrl
    console.error("Error creating shareable URL:", e);
    return absoluteUrl;
  }
}
